import type { Chart } from "../Chart";
import { Theme, Fonts } from "../theme";
import { formatPrice } from "../util";
import { computePriceTicks } from "./ticks";

export function drawPriceAxis(ctx: CanvasRenderingContext2D, chart: Chart) {
  const vp = chart.viewport;
  const x0 = vp.plot.width;
  const w = chart.priceAxisWidth;
  const h = chart.cssHeight;

  ctx.save();
  // gutter background + border
  ctx.fillStyle = Theme.axisBg;
  ctx.fillRect(x0, 0, w, h);
  ctx.strokeStyle = Theme.axisBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(x0 + 0.5, 0);
  ctx.lineTo(x0 + 0.5, h);
  ctx.stroke();

  ctx.font = Fonts.axis;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillStyle = Theme.textDim;
  const labelX = x0 + 8;
  for (const t of computePriceTicks(chart)) {
    if (t.y < 0 || t.y > vp.plot.height) continue;
    // tick mark
    ctx.strokeStyle = Theme.axisBorder;
    ctx.beginPath();
    ctx.moveTo(x0, Math.round(t.y) + 0.5);
    ctx.lineTo(x0 + 4, Math.round(t.y) + 0.5);
    ctx.stroke();
    ctx.fillStyle = Theme.textDim;
    ctx.fillText(formatPrice(t.price, chart.priceDecimals), labelX, t.y);
  }

  // last price line + tag
  const data = chart.data;
  if (data && chart.effBarCount > 0) {
    const lastIdx = chart.lastVisibleIndex;
    let close = data.closeAt(lastIdx);
    let open = data.openAt(lastIdx);
    let useIdx = lastIdx;
    if (!isFinite(close)) {
      // fall back to last visible resident bar
      const [lo, hi] = vp.visibleRange();
      for (let i = hi - 1; i >= lo; i--) {
        const c = data.closeAt(i);
        if (isFinite(c)) {
          close = c;
          open = data.openAt(i);
          useIdx = i;
          break;
        }
      }
    }
    if (isFinite(close)) {
      const y = vp.yOfPrice(close);
      const bull = close >= open;
      const color = bull ? Theme.lastPriceBull : Theme.lastPriceBear;
      if (y >= 0 && y <= vp.plot.height) {
        // dashed line across plot
        ctx.save();
        ctx.strokeStyle = color;
        ctx.globalAlpha = 0.7;
        ctx.setLineDash([2, 2]);
        ctx.beginPath();
        ctx.moveTo(0, Math.round(y) + 0.5);
        ctx.lineTo(x0, Math.round(y) + 0.5);
        ctx.stroke();
        ctx.restore();
      }
      // tag
      drawAxisTag(ctx, x0, w, y, formatPrice(close, chart.priceDecimals), color, "#fff");
    }
  }

  ctx.restore();
}

export function drawAxisTag(
  ctx: CanvasRenderingContext2D,
  x0: number,
  w: number,
  y: number,
  text: string,
  bg: string,
  fg: string
) {
  const th = 16;
  const yy = Math.max(th / 2, Math.min(y, ctx.canvas.height));
  ctx.save();
  ctx.fillStyle = bg;
  ctx.beginPath();
  const top = yy - th / 2;
  // left pointer triangle + rounded rect
  ctx.moveTo(x0, yy);
  ctx.lineTo(x0 + 5, top);
  ctx.lineTo(x0 + w, top);
  ctx.lineTo(x0 + w, top + th);
  ctx.lineTo(x0 + 5, top + th);
  ctx.closePath();
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.font = Fonts.axis;
  ctx.textBaseline = "middle";
  ctx.textAlign = "left";
  ctx.fillText(text, x0 + 9, yy);
  ctx.restore();
}
